/**
 * "Dupliceren": a copy of a page with its icon, properties, content and
 * subpages, placed right after the original. Tasks in the copy get new ids, so
 * they are tasks of their own (checking one off doesn't touch the original).
 */
import type { Id } from '@nexus/core';
import { ensureTaskIds } from '@nexus/editor';
import type { PageRecord, Store } from '@nexus/store';
import type { JSONContent } from '@tiptap/core';
import { jsonToUpdate, loadPageJSON, reindexPage } from './content';

/** The same document without task ids (they are given new ones). */
function withoutTaskIds(node: JSONContent): JSONContent {
  const attrs =
    node.type === 'taskItem' && node.attrs ? { ...node.attrs, taskId: null } : node.attrs;
  return {
    ...node,
    ...(attrs ? { attrs } : {}),
    ...(node.content ? { content: node.content.map(withoutTaskIds) } : {}),
  };
}

async function copyPage(
  store: Store,
  source: PageRecord,
  parentId: Id | null,
  title: string,
  workspaceId?: string,
): Promise<PageRecord> {
  const copy = await store.createPage({
    title,
    parentId,
    icon: source.icon,
    ...(workspaceId ? { workspaceId } : {}),
  });
  for (const [key, value] of Object.entries(await store.getProps(source.id)))
    await store.setProp(copy.id, key, value);
  const doc = ensureTaskIds(withoutTaskIds(await loadPageJSON(store, source.id))).doc;
  await store.appendContent(copy.id, jsonToUpdate(doc));
  await reindexPage(store, copy.id, doc);
  const children = (await store.listPages())
    .filter((p) => p.parentId === source.id)
    .sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));
  // workspaceId is omitted on children on purpose: createPage() inherits it
  // from their new parent (the copy), which already has it set.
  for (const child of children) await copyPage(store, child, copy.id, child.title);
  return copy;
}

export async function duplicatePage(
  store: Store,
  pageId: Id,
  titleFor: (title: string) => string = (t) => `${t} (kopie)`,
): Promise<PageRecord | null> {
  const source = await store.getPage(pageId);
  if (!source || source.deleted || source.trashedAt) return null;
  const copy = await copyPage(store, source, source.parentId, titleFor(source.title));
  await store.movePage(copy.id, source.parentId, source.id);
  return (await store.getPage(copy.id)) ?? copy;
}

/**
 * A copy of a private page (and its subtree) placed at the top level of a
 * workspace; the original is untouched. Unlike duplicatePage, no "(kopie)"
 * suffix -- it's going somewhere new, not sitting next to the original.
 */
export async function copyPageToWorkspace(
  store: Store,
  pageId: Id,
  workspaceId: string,
): Promise<PageRecord | null> {
  const source = await store.getPage(pageId);
  if (!source || source.deleted || source.trashedAt) return null;
  return copyPage(store, source, null, source.title, workspaceId);
}
